/**
 * The checkout's standard look (the prototype's checkout: forms-web.html
 * moment 1 C): the steps on the left, the summary held still on the right.
 * Draws the behavior half's state and calls its actions; holds no rule. The
 * step bodies (sign in, forms, payment, the request) are pieces of their own,
 * passed in by the page.
 */
import { useState, type ReactNode } from 'react';
import { ActiveFlowConflictOverlay } from '@/components/flowConflict/ActiveFlowConflictOverlay';
import { formatUsPhoneWithCountry } from '@/components/forms/formatPhone';
import { PHONE_NEEDED } from '@/pages/booking/shared/bookingRules';

import { partsOf, pieceRoot } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import { onLink } from '../../ui/onLink';
import { Photo } from '../../ui/Photo';
import { contract, type CheckoutActions, type CheckoutPhoneCall, type CheckoutStep } from './contract';
import type { CheckoutRuntime } from './useCheckout';
import css from './Checkout.module.css';

const part = partsOf(contract);

/** The number to call under the summary's rows (service-by-phone D5): the line with Change, or the field. */
function CallLine({ call, actions }: { call: CheckoutPhoneCall; actions: CheckoutActions }) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!call.asking) {
    return (
      <p className={`${css.hold} ${css.callLine}`} {...part('phoneCall')}>
        <Icon name="phone" small />
        <span>
          {call.line}{' '}
          <button
            type="button"
            className={css.textlink}
            onClick={() => {
              setDraft(formatUsPhoneWithCountry(call.phone ?? ''));
              setError(null);
              actions.changePhone();
            }}
          >
            Change
          </button>
        </span>
      </p>
    );
  }
  const save = async () => {
    const phone = draft.trim();
    if (!phone || saving) return;
    setSaving(true);
    setError(null);
    try {
      await actions.savePhone(phone);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'The number was not saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className={css.phoneField} {...part('phoneCall', 'asking')}>
      <label className={css.phoneLabelled}>
        <span className={css.phoneLabel}>Number to call</span>
        <span className={css.phoneInput}>
          <input type="tel" inputMode="tel" maxLength={32} autoComplete="off" value={draft} onChange={(e) => setDraft(formatUsPhoneWithCountry(e.target.value))} />
        </span>
      </label>
      {(error || call.needsNumber) && (
        <p className={`${css.hold} ${css.phoneHint}`} role={error ? 'alert' : undefined}>
          <Icon name="phone" small />
          {error ?? PHONE_NEEDED}
        </p>
      )}
      <div className={css.phoneBtns}>
        <button type="button" className={`${shared.btn} ${shared.btnPrimary} ${css.phoneBtn}`} disabled={!draft.trim() || saving} onClick={() => void save()}>
          Save
        </button>
        {!call.needsNumber && (
          <button type="button" className={`${shared.btn} ${shared.btnOutline} ${css.phoneBtn}`} disabled={saving} onClick={actions.cancelPhone}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

export interface CheckoutSlots {
  /** Each step's body while it is the current one. */
  readonly bodies: Partial<Record<CheckoutStep['id'], ReactNode>>;
  /** Once held: the hold line under the summary, its warning over the steps, and the recovery dialog. */
  readonly hold?: { readonly line: ReactNode; readonly warning: ReactNode; readonly recovery: ReactNode };
}

export function CheckoutView({ state, actions, bodies, hold }: CheckoutRuntime & CheckoutSlots) {
  const s = state.summary;
  // While forms are owed the column is the form page itself (the prototype's forms step).
  const formsBody = state.steps.some((step) => step.id === 'forms' && step.status === 'active') ? bodies.forms : undefined;
  return (
    <div className={`${shared.wrap} ${css.grid}`} {...pieceRoot(contract)} {...part('page')}>
      {formsBody ? (
        <div className={css.main}>
          {hold?.warning}
          {formsBody}
        </div>
      ) : (
      <div className={css.main}>
        {hold?.warning}
        {state.back && (
          <a className={css.back} href={state.back.href} onClick={onLink(actions.back)} {...part('back')}>
            <Icon name="back" small />
            {state.back.label}
          </a>
        )}
        <h1 className={css.h1} {...part('title')}>
          {state.title}
        </h1>
        <ol className={css.steps} {...part('steps')}>
          {state.steps.map((step) => (
            <li key={step.id} className={`${css.sec} ${css[step.status]}`} aria-current={step.status === 'active' ? 'step' : undefined} {...part('step', step.status)}>
              <div className={css.secHead}>
                <span className={css.num} aria-hidden="true">
                  {step.status === 'done' ? <Icon name="check" /> : step.number}
                </span>
                <h2 className={css.secTitle}>{step.title}</h2>
              </div>
              {step.id === 'signIn' && step.status === 'done' && state.client && (
                <div className={css.signed} {...part('signedIn')}>
                  <span className={css.avatar} aria-hidden="true">
                    {state.client.initials}
                  </span>
                  <b>{state.client.name}</b>
                  <span>Signed in with Telegram</span>
                </div>
              )}
              {step.id === 'signIn' && step.status === 'done' && (state.hold === 'taken' || state.hold === 'failed') && (
                <div className={css.body}>
                  <p className={css.problem} role="alert" {...part(state.hold === 'taken' ? 'taken' : 'problem')}>
                    {state.problem}
                  </p>
                  {state.openings.length > 0 && (
                    <div role="group" aria-label="Next openings" className={css.openings}>
                      {state.openings.map((day) => (
                        <div key={day.date} className={css.day}>
                          <div className={css.dayLabel}>{day.label}</div>
                          <div className={css.times}>
                            {day.times.map((t) => (
                              <button key={t.startsAt} type="button" className={css.time} onClick={() => actions.pickOpening(t.startsAt)} {...part('opening')}>
                                {t.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {step.status === 'active' && step.id !== 'forms' && bodies[step.id] && <div className={css.body}>{bodies[step.id]}</div>}
            </li>
          ))}
        </ol>
      </div>
      )}

      {s && (
        <aside className={css.rail} aria-label="Your booking" {...part('summary')}>
          {s.photo && (
            <div className={css.photo} {...part('photo')}>
              <Photo photo={s.photo} />
            </div>
          )}
          <div className={css.item} {...part('offering')}>
            <div>
              <div className={css.itemName}>{s.name}</div>
              {s.sub && <div className={css.itemSub}>{s.sub}</div>}
            </div>
            <div className={css.itemPrice}>{s.price}</div>
          </div>
          <dl className={css.rows} {...part('rows')}>
            {s.date && (
              <div>
                <dt>
                  <Icon name="cal" small />
                  Date
                </dt>
                <dd>{s.date}</dd>
              </div>
            )}
            {s.time && (
              <div>
                <dt>
                  <Icon name="clock" small />
                  Time
                </dt>
                <dd>{s.time}</dd>
              </div>
            )}
            {s.duration && (
              <div>
                <dt>
                  <Icon name="clock" small />
                  Duration
                </dt>
                <dd>{s.duration}</dd>
              </div>
            )}
            {s.location && (
              <div>
                <dt>
                  <Icon name={s.byPhone ? 'phone' : s.online ? 'video' : 'pin'} small />
                  Location
                </dt>
                <dd>{s.location}</dd>
              </div>
            )}
          </dl>
          {state.phoneCall && <CallLine call={state.phoneCall} actions={actions} />}
          <div className={css.total} {...part('total')}>
            <span>
              Total
              {s.totalNote && <small>{s.totalNote}</small>}
            </span>
            <span>{s.total}</span>
          </div>
          {hold?.line}
          {state.holdNote && (
            <p className={css.hold} {...part('holdNote')}>
              <Icon name="clock" small />
              {state.holdNote}
            </p>
          )}
        </aside>
      )}

      {hold?.recovery}
      {state.otherBooking && (
        <div {...part('otherBooking')}>
          <ActiveFlowConflictOverlay
            card={state.otherBooking}
            resolving={state.otherBooking.cancelling}
            onContinue={actions.continueOther}
            onStartOver={actions.startOver}
          />
        </div>
      )}
    </div>
  );
}
